import { useState } from 'react';
import { markInstallPromptDismissed } from '../hooks/useInstallPrompt';

// Modal that shows the right install path for the user's platform.
// Used in two places:
//   - Layout: auto-shows once on first mobile session if not installed +
//     not previously dismissed.
//   - ProfileDropdown: a manual "Install on phone" entry opens this any
//     time the user wants.
export default function InstallPromptModal({ open, onClose, prompt }) {
  const [installing, setInstalling] = useState(false);
  if (!open) return null;

  function handleClose({ dismiss = false } = {}) {
    if (dismiss) markInstallPromptDismissed();
    onClose();
  }

  async function handleAndroidInstall() {
    setInstalling(true);
    const { outcome } = await prompt.trigger();
    setInstalling(false);
    if (outcome === 'accepted') {
      // appinstalled handler in the hook will flip isStandalone, but
      // close the modal explicitly so the UI doesn't linger.
      handleClose({ dismiss: true });
    }
    // 'dismissed' or 'error' or 'unavailable' — leave modal open so the
    // user can see the manual fallback steps.
  }

  return (
    <div style={s.backdrop} onClick={() => handleClose()}>
      <div style={s.modal} onClick={e => e.stopPropagation()} role="dialog" aria-labelledby="install-title">
        <h2 id="install-title" style={s.title}>Install Silver Service</h2>
        <p style={s.subtitle}>
          One tap to launch fullscreen — like a real app, no browser bar.
        </p>

        {prompt.platform === 'android-chrome' && prompt.canTriggerNativePrompt && (
          <AndroidInstallBlock onInstall={handleAndroidInstall} installing={installing} />
        )}
        {prompt.platform === 'android-chrome' && !prompt.canTriggerNativePrompt && (
          <AndroidManualBlock />
        )}
        {prompt.platform === 'android-other' && <AndroidOtherBlock />}
        {prompt.platform === 'ios-safari' && <IOSBlock />}
        {prompt.platform === 'ios-other'  && <IOSOtherBlock />}
        {prompt.platform === 'desktop'    && <DesktopBlock />}

        <div style={s.actions}>
          <button style={s.dismissBtn} onClick={() => handleClose({ dismiss: true })}>
            Don't show again
          </button>
          <button style={s.closeBtn} onClick={() => handleClose()}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function AndroidInstallBlock({ onInstall, installing }) {
  return (
    <div style={s.block}>
      <p style={s.blockText}>
        Tap the button below — Chrome will ask you to confirm, then add the
        Silver Service icon to your home screen.
      </p>
      <button
        style={{ ...s.primaryBtn, ...(installing ? s.primaryBtnBusy : {}) }}
        onClick={onInstall}
        disabled={installing}
      >
        {installing ? 'Installing…' : '📲  Install on my phone'}
      </button>
    </div>
  );
}

function AndroidManualBlock() {
  return (
    <div style={s.block}>
      <p style={s.blockText}>Two-step install in Chrome:</p>
      <ol style={s.steps}>
        <Step n="1" text={<>Tap the <strong>⋮</strong> menu in the top-right of Chrome.</>} />
        <Step n="2" text={<>Tap <strong>Install app</strong> (or <strong>Add to Home Screen</strong> on older Chrome).</>} />
      </ol>
    </div>
  );
}

function AndroidOtherBlock() {
  return (
    <div style={s.block}>
      <p style={s.blockText}>
        For the best install experience on Android, we recommend Chrome.
        In your current browser, look for an "Install" or "Add to Home Screen"
        option in the browser's menu.
      </p>
    </div>
  );
}

function IOSBlock() {
  return (
    <div style={s.block}>
      <p style={s.blockText}>Three steps in Safari:</p>
      <ol style={s.steps}>
        <Step n="1" text={<>Tap the <strong>Share</strong> button at the bottom of Safari (square with an up-arrow ↑).</>} />
        <Step n="2" text={<>Scroll down and tap <strong>Add to Home Screen</strong>.</>} />
        <Step n="3" text={<>Tap <strong>Add</strong> in the top-right corner.</>} />
      </ol>
      <p style={s.iosNote}>
        <em>Apple doesn't let websites install themselves on iPhone — but those three taps in Safari are all it takes.</em>
      </p>
    </div>
  );
}

function IOSOtherBlock() {
  return (
    <div style={s.block}>
      <p style={s.blockText}>
        On iPhone, only <strong>Safari</strong> can install web apps. To
        install Silver Service:
      </p>
      <ol style={s.steps}>
        <Step n="1" text={<>Open <strong>Safari</strong> on your iPhone.</>} />
        <Step n="2" text={<>Go to <strong>orders.silverservice.com</strong>.</>} />
        <Step n="3" text={<>Tap the Share button → <strong>Add to Home Screen</strong>.</>} />
      </ol>
    </div>
  );
}

function DesktopBlock() {
  return (
    <div style={s.block}>
      <p style={s.blockText}>
        The install experience is best on phones, since most browsers prompt
        to add an app icon to the home screen. On a desktop, this is mostly
        a tab — open <strong>orders.silverservice.com</strong> on your phone
        for the full experience.
      </p>
    </div>
  );
}

function Step({ n, text }) {
  return (
    <li style={s.step}>
      <span style={s.stepNum}>{n}</span>
      <span>{text}</span>
    </li>
  );
}

const s = {
  backdrop: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 300, padding: '1rem',
    animation: 'ss-fade-in 0.15s ease-out',
  },
  modal: {
    background: '#fff', borderRadius: 12,
    width: '100%', maxWidth: 460,
    padding: '1.6rem 1.5rem 1.2rem',
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
    animation: 'ss-pop-in 0.2s ease-out',
  },
  title: { fontSize: '1.25rem', fontWeight: 700, color: '#222', margin: '0 0 0.35rem' },
  subtitle: { fontSize: '0.9rem', color: 'rgba(0,0,0,0.6)', margin: '0 0 1.25rem', lineHeight: 1.5 },
  block: {},
  blockText: { fontSize: '0.9rem', color: '#333', margin: '0 0 0.85rem', lineHeight: 1.55 },
  primaryBtn: {
    width: '100%', background: '#A50B18', color: '#fff', border: 'none',
    borderRadius: 8, padding: '0.95rem 1rem', fontSize: '1rem', fontWeight: 700,
    cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.02em',
  },
  primaryBtnBusy: { background: 'rgba(165,11,24,0.65)', cursor: 'wait' },
  steps: { listStyle: 'none', padding: 0, margin: '0 0 0.5rem', display: 'flex', flexDirection: 'column', gap: '0.65rem' },
  step: {
    display: 'flex', alignItems: 'flex-start', gap: '0.7rem',
    fontSize: '0.9rem', color: '#333', lineHeight: 1.5,
  },
  stepNum: {
    flexShrink: 0, width: 24, height: 24, borderRadius: '50%',
    background: '#A50B18', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.78rem', fontWeight: 700,
  },
  iosNote: { fontSize: '0.78rem', color: 'rgba(0,0,0,0.5)', margin: '0.85rem 0 0', lineHeight: 1.5 },
  actions: { display: 'flex', justifyContent: 'space-between', gap: '0.6rem', marginTop: '1.25rem' },
  dismissBtn: {
    background: 'none', border: 'none', color: 'rgba(0,0,0,0.45)',
    fontSize: '0.82rem', cursor: 'pointer', padding: '0.4rem 0.5rem',
    fontFamily: 'inherit',
  },
  closeBtn: {
    background: '#fff', border: '1px solid #ddd', color: '#444',
    borderRadius: 6, padding: '0.6rem 1.2rem',
    fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  },
};
